import { Action, State, StateContext, Store } from '@ngxs/store';
import { carriesAValue } from '../../../utils/an-answer-that-carries-its-value';
import {
  CreateEventAction,
  DuplicateActivityCreateEventAction,
  OnCancelCreateEventAction,
  ResetCreateEventAction,
  ResetJustCreatedAction,
  SelectAttendeesCreateEventAction,
  SelectDateAndTimeCreateEventAction,
  SelectEndLocationActivityCreateEventAction,
  SelectLocationActivityCreateEventAction,
  SelectSessionKindCreateEventAction,
  SelectSportIdCreateEventAction,
  SetAcceptCommissionCreateEventAction,
  SetBackgroundImgCreateEventAction,
  SetCommissionAndCgvCreateEventAction,
  SetCommissionRateEventAction,
  SetCostPerParticipantCreateEventAction,
  SetDescriptionCreateEventAction,
  SetDurationActivityCreateEventAction,
  SetGroupIdCreateEventAction,
  SetGroupNameCreateEventAction,
  SetPrivacyCreateEventAction,
  SetRouteUrlCreateEventAction,
  SetTitleCreateEventAction,
  SetTypeCreateEventAction,
} from './create-event.actions';
import { EventsService, SportsService } from '@smatchy/sdk';
import { GeoLocality, GeoPosition } from '../../locations';
import { parse, set } from 'date-fns';

import { ActionSheetController, ToastController } from '@ionic/angular';
import { ErrorHandler, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { eurToCents } from '../../../utils/currency.utils';
import { TranslateService } from '@ngx-translate/core';
import { catchError, tap, throwError } from 'rxjs';
import { EMAIL_NOT_CONFIRMED } from '../../user/an-address-waits-for-its-confirmation';
import { EmailConfirmationService } from '../../user/email-confirmation.service';
import { buildLocationStorageName, LocationAdminLevel } from '../../../utils/resolve-admin-level.util';

const toApiSelectedLevel = (level?: LocationAdminLevel): 'city' | 'department' | 'region' | 'country' | undefined =>
  level && level !== 'other' ? level : undefined;

export interface CreateEventStateModel {
  acceptAnyLvl: boolean;
  handiFriendly: boolean;
  attendeesMode: 'preset' | 'manual';
  bgUrl: string;
  /** Nothing is placed before the person picks a spot, which is what the defaults set. */
  coordinates?: GeoPosition;
  /** SMA-103: where the outing ends, when that is not where it starts. */
  endCoordinates?: GeoPosition;
  endLocality?: GeoLocality;
  dateStart: string;
  dateEnd: string;
  repeatWeekly: boolean;
  description: string;
  routeUrl?: string;
  distance: number;
  verticalDrop: number;
  duration: number;
  loading: boolean;
  /** Same: no city until one is chosen. */
  locality?: GeoLocality;
  maxNbAttendees: number;
  /**
   * Optional confirmation threshold, held as registrants besides the organiser
   * (one or more), like maxNbAttendees above. Undefined means no threshold.
   */
  minParticipants?: number;
  pace: string;
  sessionKindId: string;
  title: string;
  groupId?: string;
  groupName?: string;
  startHours: number;
  startMinutes: number;
  sportIdSelected: string;
  sportKeyIdSelected: string;
  eventJustCreated: boolean;
  eventIdCreated: string | undefined;
  privacy: 'public' | 'private' | 'members_only';
  type: 'paid' | 'free';
  acceptedCommission: boolean;
  costPerParticipant: number;
  commissionId?: string;
  cgvId?: string;
  commissionPercent?: number;
  /** Set while the form holds a copy of this activity, see DuplicateActivityCreateEventAction. */
  duplicatedFromId?: string;
}

const defaults: CreateEventStateModel = {
  sportKeyIdSelected: '',
  startHours: 0,
  startMinutes: 0,
  coordinates: undefined,
  locality: undefined,
  endCoordinates: undefined,
  endLocality: undefined,
  dateStart: '',
  sessionKindId: '',
  distance: 0,
  duration: 0,
  verticalDrop: 0,
  pace: '',
  loading: false,
  bgUrl: '',
  title: '',
  sportIdSelected: '',
  description: '',
  groupId: undefined,
  groupName: '',
  routeUrl: undefined,
  maxNbAttendees: 0,
  minParticipants: undefined,
  // « Tous niveaux » is ticked by default (Maude, 22/10/2026, Asana 1218854524230355):
  // an activity that turns nobody away is the better default for whoever creates it.
  acceptAnyLvl: true,
  handiFriendly: false,
  attendeesMode: 'preset',
  eventIdCreated: undefined,
  eventJustCreated: false,
  privacy: 'public',
  repeatWeekly: false,
  dateEnd: '',
  type: 'free',
  acceptedCommission: false,
  costPerParticipant: 0,
  commissionId: undefined,
  cgvId: undefined,
  commissionPercent: undefined,
  duplicatedFromId: undefined,
};

/**
 * One place, one shape: what the API stores for a point on a map.
 *
 * SMA-103 gave the activity a second one, and two copies of this that drift is
 * how an arrival ends up written differently from a departure.
 */
const asApiLocation = (coordinates?: GeoPosition, locality?: GeoLocality) => ({
  /*
   * Both optional, because the state holds neither before the person picks a
   * spot -- one read here already wrote `locality?.` while the five beside it
   * did not, which is the sort of half-guard this ticket keeps finding.
   *
   * `?? 0` on the two numbers rather than a refusal: this builds the location
   * an activity is created with, and a creation without a place is stopped by
   * the form long before it reaches here.
   */
  lat: coordinates?.lat ?? 0,
  lon: coordinates?.lng ?? 0,
  name: buildLocationStorageName(locality?.hierarchy, locality?.formattedAddress || locality?.suggestionDetail),
  placeId: locality?.placeId,
  selectedLevel: toApiSelectedLevel(locality?.selectedLevel || locality?.adminLevel),
  selectedName: locality?.selectedName || locality?.suggestionPrimary || locality?.city,
  hierarchy: locality?.hierarchy,
});

@State<CreateEventStateModel>({
  name: 'createEvent',
  defaults,
})
@Injectable()
export class CreateEventState {
  constructor(
    private readonly _store: Store,
    private readonly _sportService: SportsService,
    private readonly _eventsService: EventsService,
    private actionSheetCtrl: ActionSheetController,
    private readonly _router: Router,
    private translate: TranslateService,
    private readonly toastController: ToastController,
    // The app binds this to Sentry in app.module. Going through Angular rather
    // than importing the tracing module keeps Sentry.init out of the tests,
    // where it would start a real client and patch the injector.
    private readonly errorHandler: ErrorHandler,
    // Last and optional, so the states built by hand in the specs stay as they are.
    private readonly emailConfirmation?: EmailConfirmationService,
  ) {}

  @Action(ResetCreateEventAction)
  async resetCreateEventAction({ patchState, getState }: StateContext<CreateEventStateModel>) {
    const state = getState();
    patchState({
      ...defaults,
      eventJustCreated: state.eventJustCreated,
    });
  }

  @Action(DuplicateActivityCreateEventAction)
  duplicateActivityCreateEventAction(
    { setState }: StateContext<CreateEventStateModel>,
    { payload }: DuplicateActivityCreateEventAction,
  ) {
    // Starts from the defaults, not from whatever an abandoned creation left.
    setState({
      ...defaults,
      ...payload,
    });
  }

  @Action(ResetJustCreatedAction)
  async resetJustCreatedAction({ patchState }: StateContext<CreateEventStateModel>) {
    patchState({
      eventJustCreated: false,
    });
  }

  @Action(SetTitleCreateEventAction)
  async setTitleCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetTitleCreateEventAction,
  ) {
    patchState({
      title: payload,
    });
  }

  @Action(SetPrivacyCreateEventAction)
  async setPrivacyCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetPrivacyCreateEventAction,
  ) {
    patchState({
      privacy: payload,
    });
  }

  @Action(SelectDateAndTimeCreateEventAction)
  async selectDateAndTimeCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SelectDateAndTimeCreateEventAction,
  ) {
    patchState({
      dateStart: payload.date,
      startHours: payload.startHours,
      startMinutes: payload.startMinutes,
      repeatWeekly: payload.repeatWeekly,
      dateEnd: payload.dateEnd,
    });
  }

  @Action(SetBackgroundImgCreateEventAction)
  async setBackgroundImgCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetBackgroundImgCreateEventAction,
  ) {
    patchState({
      bgUrl: payload,
    });
  }

  @Action(SetGroupIdCreateEventAction)
  async setGroupIdCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetGroupIdCreateEventAction,
  ) {
    patchState({
      groupId: payload,
    });
  }

  @Action(SetGroupNameCreateEventAction)
  async setGroupNameCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetGroupNameCreateEventAction,
  ) {
    patchState({
      groupName: payload,
    });
  }

  @Action(SelectSportIdCreateEventAction)
  async selectSportIdCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SelectSportIdCreateEventAction,
  ) {
    patchState({
      sportIdSelected: payload.sportId,
      sportKeyIdSelected: payload.sportKeyId,
    });
  }

  @Action(SelectSessionKindCreateEventAction)
  async selectSessionKindCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SelectSessionKindCreateEventAction,
  ) {
    patchState({
      sessionKindId: payload,
    });
  }

  @Action(SetDurationActivityCreateEventAction)
  async setDurationActivityCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetDurationActivityCreateEventAction,
  ) {
    patchState({
      duration: payload.duration,
      distance: payload.distance,
      pace: payload.pace,
      verticalDrop: payload.verticalDrop,
    });
  }

  @Action(SelectLocationActivityCreateEventAction)
  async selectLocationActivityCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SelectLocationActivityCreateEventAction,
  ) {
    patchState({
      coordinates: payload.coordinates,
      locality: payload.locality,
    });
  }

  @Action(SelectEndLocationActivityCreateEventAction)
  async selectEndLocationActivityCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SelectEndLocationActivityCreateEventAction,
  ) {
    patchState({
      endCoordinates: payload?.coordinates,
      endLocality: payload?.locality,
    });
  }

  @Action(SelectAttendeesCreateEventAction)
  async selectAttendeesCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SelectAttendeesCreateEventAction,
  ) {
    patchState({
      maxNbAttendees: payload.maxAttendees,
      minParticipants: payload.minParticipants,
      acceptAnyLvl: payload.acceptAnyLvl,
      handiFriendly: payload.handiFriendly ?? false,
      attendeesMode: payload.attendeesMode,
    });
  }

  @Action(SetDescriptionCreateEventAction)
  async setDescriptionCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetDescriptionCreateEventAction,
  ) {
    patchState({
      description: payload,
    });
  }

  @Action(SetRouteUrlCreateEventAction)
  async setRouteUrlCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetRouteUrlCreateEventAction,
  ) {
    const routeUrl = payload?.trim() || undefined;
    patchState({
      routeUrl,
    });
  }

  @Action(CreateEventAction)
  async createEventAction({ patchState, getState }: StateContext<CreateEventStateModel>) {
    const state = getState();
    patchState({
      loading: true,
    });
    const startDate = parse(state.dateStart, 'dd/MM/yyyy', new Date());

    return this._eventsService
      .createEvent({
        createEventDto: {
          acceptAnyLvl: state.acceptAnyLvl,
          handiFriendly: state.handiFriendly,
          attendeesMode: state.attendeesMode,
          backgroundUrl: state.bgUrl,
          description: state.description,
          distance: state.distance,
          duration: state.duration,
          verticalDrop: state.verticalDrop,
          // FIXME: at this time the level is calculated by server
          // level: this._store.selectSnapshot(UserProfileSelector.userSportLevel(state.sportKeyIdSelected))?.level || 1,
          level: 0,
          location: asApiLocation(state.coordinates, state.locality),
          // SMA-103. Absent on almost every activity, and the field is only
          // sent when both halves are there: a point without its address, or
          // an address without its point, would draw a row that says nothing
          // and opens nowhere.
          ...(state.endCoordinates && state.endLocality
            ? { endLocation: asApiLocation(state.endCoordinates, state.endLocality) }
            : {}),
          maxNbAttendees: state.maxNbAttendees + 1, // +1 for the creator
          // +1 for the creator too, so the threshold the API stores counts the
          // organiser in (the form holds it as registrants besides them).
          minParticipants: state.minParticipants ? state.minParticipants + 1 : undefined,
          pace: state.pace,
          sessionKindId: state.sessionKindId,
          startDate: set(startDate, {
            hours: state.startHours,
            minutes: state.startMinutes,
          }).toISOString(),
          sportId: state.sportIdSelected,
          title: state.title,
          groupId: state.groupId,
          privacy: state.privacy,
          routeUrl: state.routeUrl,
          repeatWeekly: state.repeatWeekly,
          endDate: state.dateEnd ? parse(state.dateEnd, 'dd/MM/yyyy', new Date()).toISOString() : undefined,
          type: state.type,
          costPerParticipant: state.costPerParticipant ? eurToCents(state.costPerParticipant) : undefined,
          commissionPercent: state.commissionPercent,
        },
      })
      .pipe(
        tap((res) => {
          // The API answers 200 with an error field for a refusal it expected,
          // and a 4xx for one it did not. Both used to end here: the first in a
          // console nobody reads, the second not at all, in the error channel
          // this pipe did not have. Either way the button kept spinning, no
          // activity existed, and nothing said so.
          if (!carriesAValue(res)) {
            throw res.error;
          }
          patchState({
            loading: false,
            eventIdCreated: res.value.event._id,
            eventJustCreated: true,
          });

          // dispatch(new ResetCreateEventAction());
        }),
        catchError((error: unknown) => {
          patchState({
            loading: false,
            eventIdCreated: undefined,
            eventJustCreated: false,
          });
          this.errorHandler.handleError(error);
          void this.showCreationError(error);

          // Rethrown on purpose: the screens wait for this before moving to the
          // final one, and an activity that does not exist must not be
          // celebrated.
          return throwError(() => error);
        }),
      );
  }

  /**
   * What a refused creation looks like from the outside.
   *
   * A refused location gets its own sentence: it is the one the person can act
   * on, and the one production actually answers, several times a day. The rest
   * share a neutral message, because naming a cause the person cannot check
   * helps nobody.
   */
  private async showCreationError(error: unknown): Promise<void> {
    // Offline, app.component already covers the screen with its own modal. A
    // toast on top of it would say the same thing twice, the second time less
    // clearly. The failure still reaches Sentry above.
    if (!navigator.onLine) {
      return;
    }

    const text = JSON.stringify(error ?? '');
    // A paid activity waits for a confirmed address (Asana 1218325456154102):
    // the person is told why, and given the way to the link.
    if (text.includes(EMAIL_NOT_CONFIRMED) && this.emailConfirmation) {
      await this.emailConfirmation.tellTheActionWaits();
      return;
    }
    const locationRefused =
      text.includes('activity_location_invalid') || text.includes('activity_location_level_not_allowed');

    const toast = await this.toastController.create({
      message: this.translate.instant(
        locationRefused ? 'CREATE_EVENT.ERRORS.LOCATION_REFUSED' : 'CREATE_EVENT.ERRORS.CREATION_FAILED',
      ),
      // Longer than the app's usual 1500ms: this one asks the person to do
      // something, so it has to outlast a glance.
      duration: 5000,
      icon: 'warning',
      position: 'top',
      cssClass: 'toast-error',
    });
    await toast.present();
  }

  @Action(OnCancelCreateEventAction)
  async onCancelCreateEventAction(
    { dispatch }: StateContext<CreateEventStateModel>,
    { payload }: OnCancelCreateEventAction,
  ) {
    const actionSheet = await this.actionSheetCtrl.create({
      header: this.translate.instant(payload.title ?? 'CREATE_EVENT.CANCEL_POPUP.TITLE'),
      cssClass: 'cancel-event-action-sheet',
      buttons: [
        {
          text: this.translate.instant('CREATE_EVENT.CANCEL_POPUP.CANCEL'),
          cssClass: 'danger',
          handler: () => {
            if (payload.mode === 'modal') {
              // `?.` because the action declares it optional and null: the
              // modal supplies one, the full screen does not.
              payload.fctBack?.emit();
            } else {
              dispatch(new ResetCreateEventAction());
              this._router.navigate(['/']);
            }
          },
        },
        {
          text: this.translate.instant('CREATE_EVENT.CANCEL_POPUP.BACK'),
          handler: () => {},
        },
      ],
    });

    await actionSheet.present();
  }

  @Action(SetTypeCreateEventAction)
  async setTypeCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetTypeCreateEventAction,
  ) {
    patchState({
      type: payload,
    });
  }
  @Action(SetAcceptCommissionCreateEventAction)
  async setAcceptCommissionCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetAcceptCommissionCreateEventAction,
  ) {
    patchState({
      acceptedCommission: payload,
    });
  }
  @Action(SetCostPerParticipantCreateEventAction)
  async setCostPerParticipantCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetCostPerParticipantCreateEventAction,
  ) {
    patchState({
      costPerParticipant: payload,
    });
  }
  @Action(SetCommissionAndCgvCreateEventAction)
  async setCommissionAndCgvCreateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetCommissionAndCgvCreateEventAction,
  ) {
    patchState({
      commissionId: payload.commissionId,
      cgvId: payload.cgvId,
    });
  }
  @Action(SetCommissionRateEventAction)
  async setCommissionRateEventAction(
    { patchState }: StateContext<CreateEventStateModel>,
    { payload }: SetCommissionRateEventAction,
  ) {
    patchState({
      commissionPercent: payload.commissionRate,
    });
  }
}
